// Twin of town-square/src/lib/dmImageUpload.ts — edit both or neither.
import type { ApolloClient } from '@apollo/client';
import { putPresignedFile, type UploadContext } from '@arthelper/ui';
import { atStep } from '@arthelper/shared/upload-failure';

import { CREATE_IMAGE_MUTATION, GENERATE_SIGNED_URL } from '~/graphql/mutations';

export {
  ALLOWED_DM_ATTACHMENT_TYPES,
  MAX_DM_ATTACHMENT_BYTES,
  MAX_DM_ATTACHMENTS,
  MAX_DM_FILE_BYTES,
  DM_ATTACHMENT_ACCEPT,
  isAllowedDmAttachment,
  isAllowedDmFile,
  classifyDmAttachments,
  dmFileMediaType,
  partitionDmAttachments,
} from '@arthelper/shared/dm-attachments';

import { dmFileMediaType } from '@arthelper/shared/dm-attachments';
import { uploadOrigin } from '@arthelper/shared/upload-origin';

/** A non-image DM attachment already uploaded to the sender's private bucket. */
export interface DmFileAttachment {
  url: string;
  name: string;
  mediaType: string;
  size: number;
}

function getDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: 0, height: 0 });
    };
    img.src = objectUrl;
  });
}

async function presign(
  client: ApolloClient<object>,
  file: File,
  type: 'public' | 'private',
  contentType?: string
): Promise<string> {
  const { data } = await client.mutate({
    mutation: GENERATE_SIGNED_URL,
    variables: { input: { fileName: file.name, fileType: contentType || file.type, type } },
  });
  const url: string | undefined = data?.generateSignedUrl;
  if (!url) throw new Error('presign-failed');
  return url;
}

/**
 * Upload a single non-image file (PDF, doc, spreadsheet) as a DM attachment:
 * presign + PUT to the PRIVATE bucket under the sender's own prefix — no
 * createImage, no image processing. The stored URL is signed on read; the
 * composer preview is a filename chip. Reuses the same `generateSignedUrl`
 * primitive the Assistant chat uses for its file uploads.
 */
export async function uploadDmFile(
  client: ApolloClient<object>,
  file: File,
  ctx: UploadContext
): Promise<DmFileAttachment> {
  const mediaType = dmFileMediaType(file);
  const privateSignedUrl = await presign(client, file, 'private', mediaType);
  await putPresignedFile(file, { url: privateSignedUrl, contentType: mediaType }, ctx.signal, ctx.onProgress);
  const url = privateSignedUrl.split('?')[0];
  return { url, name: file.name, mediaType, size: file.size };
}

/**
 * Upload a single image as a DM attachment: presign + PUT to the private bucket
 * only, then createImage with context 'message'. The artwork processor writes
 * optimized/thumbnail WebPs to the same private bucket and skips Slack/AI/
 * embedding/copyright fan-out. GraphQL signs those URLs on read. The composer
 * preview is the caller's blob URL — nothing here is world-readable. Resolves
 * right after createImage: the thread renders a processing placeholder, so the
 * send never waits on (or fails with) image processing.
 * Calls `trackImageUploaded(imageId)` after createImage — the caller wires it
 * to the `image_uploaded` analytics event (this identifier is also what the
 * upload-events gate scans this file for).
 */
export async function uploadDmImage(
  client: ApolloClient<object>,
  file: File,
  trackImageUploaded: (imageId: string) => void,
  ctx: UploadContext
): Promise<{ imageId: string }> {
  const { width, height } = await atStep('pick', () => getDimensions(file));

  const privateSignedUrl = await atStep('signed_url', () => presign(client, file, 'private'));
  await atStep('s3_put', () =>
    putPresignedFile(file, { url: privateSignedUrl, contentType: file.type }, ctx.signal, ctx.onProgress)
  );
  const originalUrl = privateSignedUrl.split('?')[0];

  const { data } = await atStep('create', () =>
    client.mutate({
      mutation: CREATE_IMAGE_MUTATION,
      variables: {
        input: {
          originalUrl,
          // Not artwork — a DM attachment. Kept out of My Artwork and public surfaces.
          context: 'message',
          ...uploadOrigin('direct_message'),
          originalWidth: width,
          originalHeight: height,
          status: 'uploaded',
          fileMeta: {
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            originalWidth: width,
            originalHeight: height,
            uploadedAt: new Date().toISOString(),
          },
        },
      },
    })
  );

  const created = data?.createImage;
  if (!created?.id) throw new Error('create-image-failed');

  trackImageUploaded(String(created.id));
  return { imageId: String(created.id) };
}
